import React, {useContext, useEffect} from "react";
import Grid from "@mui/material/GridLegacy";
import TextField from "@mui/material/TextField";
import {CircularProgress, InputAdornment} from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {NotificationType} from "../../../constants/global";
import {editUserValidationSchema} from "../../../constants/validationSchemas";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {PharmacyContext} from "../../../hooks/pharmacyContext";
import {ToasterContext} from "../../../components/shared/Toaster/ToasterContext";
import {useNavigate, useParams} from "react-router-dom";
import {routeConfigs} from "../../../navigation/routeConfig";
import {PHARMACY_USERS} from "../../../queries/pharmacy/pharmacyTypeDefs";
import {useMutationUpdateUser} from "../../../queries/user/hooks/useMutationUpdateUser";
import {useQueryUserById} from "../../../queries/user/hooks/useQueryUserById";
import BrandLoader from "../../../components/shared/BrandLoader/BrandLoader";

export const EditUser:React.FunctionComponent = () => {
  const { id: userId } = useParams<any>();
  const {addToast} = useContext(ToasterContext);
  const navigate  = useNavigate();
  const { pharmacyId }: any = useContext(PharmacyContext);

  const {loading, data} = useQueryUserById({
    variables: { where: {id: Number(userId)} },
  })

  const [updateUser, {loading: updateLoading}] = useMutationUpdateUser({
    refetchQueries: [
      {
        query: PHARMACY_USERS,
        variables: {
          where: { Pharmacy: { is: { id: { equals: +pharmacyId } } } },
          orderBy: [ { createdAt: "asc" } ]
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: yupResolver(editUserValidationSchema)
  });

  const onSubmit = async (data: any) => {
    const requestData = {
      firstName: {set: data.firstName},
      lastName: {set: data.lastName},
      email: {set: data.email},
      phone: {set: `+1${data.phone}`},
    }
    updateUser({ variables: { data: requestData, where: {id: Number(userId)} } }).then(() => {
      addToast({ type: NotificationType.Success, title: 'User created successfully' });
      navigate(routeConfigs.users.path);
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  };

  useEffect(() => {
    if (data) {
      setValue('email', data.findUniqueUser.email);
    }
  },[data, setValue]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <BrandLoader size={48} />
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="md" sx={{mb: 4}}>
      <Paper sx={{p: 4}}>
        <Box sx={{ pb: 3 }}>
          <Typography component="h4" variant="h6">Edit user</Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 2}}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="First Name"
                id="firstName"
                fullWidth
                required
                defaultValue={data.findUniqueUser.firstName}
                helperText={errors.firstName?.message?.toString()}
                {...register('firstName')}
                error={!!errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="family-name"
                label="Last Name"
                id="lastName"
                fullWidth
                required
                defaultValue={data.findUniqueUser.lastName}
                helperText={errors.lastName?.message?.toString()}
                {...register('lastName')}
                error={!!errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="email"
                label="Email Address"
                id="email"
                fullWidth
                required
                disabled
                defaultValue={data.findUniqueUser.email}
                {...register('email')}
                // helperText={errors.email?.message?.toString()}
                // error={!!errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="phone"
                label="Phone number"
                id="phone"
                fullWidth
                required
                defaultValue={data.findUniqueUser.phone.replace('+1', '')}
                helperText={errors.phone?.message?.toString()}
                {...register('phone')}
                error={!!errors.phone}
                InputProps={{
                  startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{mt: 3, mb: 2}}
          >
            Save
            { updateLoading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default EditUser;